import { Typography, Breadcrumb, Card, Row, Col, Statistic, Table, Button, Space, Tag, Spin } from 'antd'
import {
  TeamOutlined,
  BankOutlined,
  LockOutlined,
  UserAddOutlined,
  ClockCircleOutlined,
  UserOutlined,
  UserDeleteOutlined,
  AppstoreOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ru'

import { useAuthStore } from '@/store/auth'
import { useAdminOverview } from '@/hooks/useAdminDashboard'
import type { AuditEntry } from '@/api/adminDashboard'

dayjs.extend(relativeTime)
dayjs.locale('ru')

const { Title } = Typography

const ACTION_LABELS: Record<string, string> = {
  create: 'Создание',
  update: 'Изменение',
  delete: 'Удаление',
  deactivate: 'Деактивация',
  reactivate: 'Активация',
  login: 'Вход',
}

const ENTITY_LABELS: Record<string, string> = {
  user: 'Сотрудник',
  organization: 'Организация',
  organization_capabilities: 'Возможности организации',
  access_group: 'Группа доступа',
  invite: 'Приглашение',
  rls_binding: 'RLS-привязка',
}

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  deactivate: 'orange',
  reactivate: 'cyan',
  login: 'default',
}

export default function AdminDashboardPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isSuperadmin = user?.role === 'superadmin'

  const { data, isLoading } = useAdminOverview()

  const auditColumns: ColumnsType<AuditEntry> = [
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (action: string) => (
        <Tag color={ACTION_COLORS[action] || 'default'}>
          {ACTION_LABELS[action] || action}
        </Tag>
      ),
    },
    {
      title: 'Объект',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 200,
      render: (entityType: string) =>
        ENTITY_LABELS[entityType] || entityType,
    },
    {
      title: 'Кто',
      key: 'user',
      ellipsis: true,
      render: (_: unknown, record: AuditEntry) =>
        record.user_name || record.user_email || '\u2014',
    },
    {
      title: 'Когда',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (dt: string | null) =>
        dt ? dayjs(dt).fromNow() : '\u2014',
    },
  ]

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Сводка' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Title level={4} style={{ marginBottom: 24 }}>
        Панель администратора
      </Title>

      {/* ── Statistic cards ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/users')}>
            <Statistic
              title="Активные сотрудники"
              value={data?.users_active ?? 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/users')}>
            <Statistic
              title="Неактивные сотрудники"
              value={data?.users_inactive ?? 0}
              prefix={<UserDeleteOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>

        {isSuperadmin && (
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Организации"
                value={data?.orgs_count ?? 0}
                prefix={<BankOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        )}

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/access-groups')}>
            <Statistic
              title="Группы доступа"
              value={data?.groups_count ?? 0}
              prefix={<LockOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Quick actions ── */}
      <Card
        title="Быстрые действия"
        style={{ marginTop: 24 }}
        size="small"
      >
        <Space wrap>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => navigate('/admin/users')}
          >
            Добавить сотрудника
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={() => navigate('/admin/access-groups')}
          >
            Группы доступа
          </Button>
          <Button
            icon={<AppstoreOutlined />}
            onClick={() => navigate('/admin/bi-workspace')}
          >
            BI-центр
          </Button>
        </Space>
      </Card>

      {/* ── Recent audit entries ── */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Последние действия</span>
          </Space>
        }
        style={{ marginTop: 24 }}
        size="small"
      >
        <Table
          columns={auditColumns}
          dataSource={data?.recent_audit || []}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: 'Нет записей' }}
        />
      </Card>
    </div>
  )
}
